import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input } from 'antd';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useClientRecord } from './client-record-data';
import { EmptyValue, InitialsAvatar, RecordCard } from './record-primitives';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/drivers')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <DriversTab />
        </PageController>
    );
}

type DriverQueryParams = RouterInputs['fleetClientDb']['listFleetDrivers'];
type DriverRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetDrivers']>['rows'][number];
const columnHelper = createColumnHelper<DriverRow, NonNullable<DriverQueryParams['orderBy']>>();

function DriversTab() {
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const { formatDate } = useFormatter();
    const [params, setParams] = useState<DriverQueryParams>({
        limit: 20,
        orderBy: { name: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');

    const isClient = client ? !isConsolidatorRecord(client) : false;
    const driversQuery = trpc.fleetClientDb.listFleetDrivers.useQuery(
        isClient ? { ...params, where: { ...params.where, clientId: id } } : skipToken,
    );

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: driversQuery.data?.rows || [],
        total: driversQuery.data?.total || 0,
        loading: driversQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Driver',
                sortable: 'name',
                cell: ({ row }) => (
                    <div className="flex items-center gap-2.5">
                        <InitialsAvatar name={row.name} size="small" />
                        <span className="font-semibold">{row.name}</span>
                    </div>
                ),
            }),
            columnHelper.accessor('vehiclesCount', {
                id: 'vehiclesCount',
                header: 'Vehicles',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.vehiclesCount}</span>,
            }),
            columnHelper.accessor('mobile', {
                id: 'mobile',
                header: 'Mobile',
                width: 150,
                cell: ({ row }) =>
                    row.mobile ? <span className="tabular-nums">{row.mobile}</span> : <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('email', {
                id: 'email',
                header: 'Email',
                cell: ({ row }) =>
                    row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('licenceNo', {
                id: 'licence',
                header: 'Licence',
                width: 170,
                cell: ({ row }) => {
                    if (!row.licenceNo) {
                        return <EmptyValue>—</EmptyValue>;
                    }
                    const detail = [
                        row.licenceState,
                        row.licenceExpiry ? `expires ${formatDate(row.licenceExpiry)}` : null,
                    ]
                        .filter(Boolean)
                        .join(', ');
                    return (
                        <div className="flex flex-col">
                            <span className="tabular-nums">{row.licenceNo}</span>
                            {detail ? <span className="text-subtle-foreground text-xs">{detail}</span> : null}
                        </div>
                    );
                },
            }),
        ],
    });

    if (client && !isClient) {
        return null;
    }

    const count = client?.driversCount ?? 0;

    return (
        <RecordCard
            padded={false}
            footer={
                <>
                    <Info className="size-3.5 shrink-0" />
                    Mobile, email and licence details arrive with the carparc feed. The current import supplies driver
                    names only.
                </>
            }
        >
            <div className="flex flex-wrap items-center gap-2 border-separator border-b px-4 py-3">
                <Input.Search
                    placeholder="Search by driver name"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) =>
                        setParams((prev) => ({
                            ...prev,
                            offset: 0,
                            where: {
                                ...prev.where,
                                name: value.trim() ? { contains: value.trim(), mode: 'insensitive' } : undefined,
                            },
                        }))
                    }
                />
                <span className="ml-auto text-subtle-foreground text-xs">
                    {count} {count === 1 ? 'driver' : 'drivers'} · derived from the client's vehicles
                </span>
            </div>
            {driversQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={driversQuery.error} onRetry={() => void driversQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable {...tableProps} />
            )}
        </RecordCard>
    );
}
